/**
 * Smart WebSocket hook for user-modern — anonymous token + content_update (aligned with user-classic).
 * Uses fetch for anonymous token (no ApiRequestManager).
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import Swal from 'sweetalert2'

interface BalanceNotificationData {
  type: string
  userId: string
  username: string
  adjustmentAmount: number
  adjustmentType: 'increase' | 'decrease'
  adjustmentCategory: string
  reason: string
  newBalance: string | number
  adjustedBy: string
  timestamp: string
  message: string
}

interface ContentUpdateData {
  type: string
  tenantId?: string
  content: unknown
  contentType?: string
  updatedBy?: string
  timestamp?: string
}

interface MaintenanceSocketEvent {
  tenantId?: string | null
  enabled?: boolean
  reason?: string
  estimatedEnd?: string | null
  maintenanceStart?: string | null
  countdownMinutes?: number
}

export interface UseSmartWebSocketProps {
  tenantId: string
  onContentUpdate: (content: unknown) => void
  enabled?: boolean
}

interface TokenCache {
  token: string
  expiresAt: number
  tenantId: string
}

const getAuthToken = (): string | null =>
  typeof localStorage !== 'undefined'
    ? localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      null
    : null

const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp > Math.floor(Date.now() / 1000)
  } catch {
    return false
  }
}

let anonymousTokenCache: TokenCache | null = null
let lastTokenRequest = 0
const minTokenInterval = 3000
const USER_PRESENCE_PING_MS = 45000

async function getAnonymousToken(tenantId: string): Promise<string | null> {
  try {
    if (
      anonymousTokenCache &&
      anonymousTokenCache.tenantId === tenantId &&
      Date.now() < anonymousTokenCache.expiresAt - 60000
    ) {
      if (import.meta.env.DEV) console.log('🎟️ Using cached anonymous token')
      return anonymousTokenCache.token
    }
    const now = Date.now()
    if (now - lastTokenRequest < minTokenInterval) {
      if (import.meta.env.DEV) console.log('🚫 Anonymous token request skipped (rate limit)')
      return anonymousTokenCache?.token ?? null
    }
    lastTokenRequest = now

    const baseURL =
      import.meta.env.VITE_API_BASE_URL ||
      (typeof window !== 'undefined' ? '' : 'http://localhost:8081')
    const apiPath = `${baseURL}/api/v1/websocket-gateway/anonymous-token`
    if (import.meta.env.DEV) {
      console.log('🎟️ Requesting new anonymous token...', { tenantId, apiPath })
    }

    const response = await fetch(apiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantId,
        purpose: 'content_updates',
        scope: ['websocket', 'firebase-config'],
      }),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    const data = (await response.json()) as {
      success?: boolean
      token?: string
      tenantId?: string
      expiresIn?: number
      scope?: string[]
      sessionId?: string
      error?: string
    }
    if (data.success && data.token) {
      if (import.meta.env.DEV) {
        console.log('✅ Anonymous token received from WebSocket Gateway', {
          tenantId: data.tenantId,
          expiresIn: data.expiresIn,
          scope: data.scope,
          sessionId: data.sessionId,
        })
      }
      anonymousTokenCache = {
        token: data.token,
        expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000,
        tenantId,
      }
      return data.token
    }
    if (import.meta.env.DEV) console.error('❌ Anonymous token response error:', data.error)
    anonymousTokenCache = null
    return null
  } catch (err) {
    console.error('❌ Failed to get anonymous token:', err)
    anonymousTokenCache = null
    return null
  }
}

export function useSmartWebSocket({
  tenantId,
  onContentUpdate,
  enabled = true,
}: UseSmartWebSocketProps) {
  const socketRef = useRef<Socket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const presenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 3
  const lastFailureTime = useRef(0)
  const circuitBreakerThreshold = 30000
  const [isConnected, setIsConnected] = useState(false)
  const [authType, setAuthType] = useState<'anonymous' | 'authenticated' | null>(null)
  const isConnectingRef = useRef(false)
  const connectionIdRef = useRef('')
  const tenantIdRef = useRef(tenantId)
  const isKickedRef = useRef(false)
  const onContentUpdateRef = useRef(onContentUpdate)
  const connectRef = useRef<(force?: boolean) => Promise<void>>(async () => {})
  onContentUpdateRef.current = onContentUpdate

  useEffect(() => {
    tenantIdRef.current = tenantId
  }, [tenantId])

  const clearPresencePing = useCallback(() => {
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current)
      presenceIntervalRef.current = null
    }
  }, [])

  const emitPresencePing = useCallback((socket: Socket, connectionType: 'authenticated' | 'anonymous') => {
    if (connectionType !== 'authenticated' || !socket.connected) return
    socket.emit('user_presence_ping', {
      tenantId: tenantIdRef.current,
      timestamp: new Date().toISOString(),
    })
  }, [])

  const startPresencePing = useCallback((socket: Socket, connectionType: 'authenticated' | 'anonymous') => {
    clearPresencePing()
    if (connectionType !== 'authenticated') return
    emitPresencePing(socket, connectionType)
    presenceIntervalRef.current = setInterval(() => {
      emitPresencePing(socket, connectionType)
    }, USER_PRESENCE_PING_MS)
  }, [clearPresencePing, emitPresencePing])

  const handleUserKicked = useCallback(async (reason: string, message: string) => {
    isKickedRef.current = true
    clearPresencePing()
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    reconnectAttempts.current = maxReconnectAttempts
    const displayMessage = message || 'คุณจะถูกนำกลับไปหน้าเข้าสู่ระบบ'

    await Swal.fire({
      icon: 'warning',
      title: 'คุณถูกเตะออกจากระบบ',
      html: `
        <div style="text-align: center; padding: 20px;">
          <div style="font-size: 18px; margin-bottom: 15px; color: #dc2626; font-weight: bold;">
            <strong>ผู้ดูแลระบบได้เตะคุณออกจากระบบ</strong>
          </div>
          <div style="color: #374151; margin-bottom: 15px; white-space: pre-wrap; word-wrap: break-word; text-align: center; font-size: 16px; line-height: 1.6;">
            ${displayMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;')}
          </div>
          <div style="color: #6b7280; font-size: 14px; font-weight: 600; text-align: center;">
            กำลังนำคุณกลับไปหน้าแรกใน <span id="countdown-kicked" style="font-size: 18px; font-weight: bold;">5</span> วินาที...
          </div>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 5000,
      timerProgressBar: true,
      background: '#fff',
      didOpen: () => {
        let timeLeft = 5
        const el = document.getElementById('countdown-kicked')
        const t = setInterval(() => {
          timeLeft--
          if (el) el.textContent = String(timeLeft)
          if (timeLeft <= 0) clearInterval(t)
        }, 1000)
      },
    })

    if (socketRef.current) {
      try {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
      } catch (e) {
        console.warn('Error closing socket:', e)
      }
      socketRef.current = null
    }
    if ((window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket) {
      (window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket = undefined
    }
    window.dispatchEvent(
      new CustomEvent('user_kicked', { detail: { reason, message } })
    )
    try {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('authToken')
      sessionStorage.clear()
    } catch (e) {
      console.warn('Error clearing auth data:', e)
    }
    window.location.hash = '#/'
    window.location.reload()
  }, [clearPresencePing])

  const detectAuthStatus = useCallback((): {
    isAuthenticated: boolean
    token: string | null
    authType: 'authenticated' | 'anonymous'
  } => {
    const authToken = getAuthToken()
    if (authToken && isTokenValid(authToken)) {
      return { isAuthenticated: true, token: authToken, authType: 'authenticated' }
    }
    if (import.meta.env.DEV) {
      console.log('🎟️ No valid auth token, using anonymous mode', {
        hasAccessToken: !!localStorage.getItem('accessToken'),
        hasToken: !!localStorage.getItem('token'),
        hasAuthToken: !!localStorage.getItem('authToken'),
        accessTokenPreview: localStorage.getItem('accessToken')?.slice(0, 20) ?? null,
      })
    }
    return { isAuthenticated: false, token: null, authType: 'anonymous' }
  }, [])

  const attemptReconnect = useCallback(() => {
    if (isKickedRef.current || !enabled) return
    const now = Date.now()
    if (now - lastFailureTime.current < circuitBreakerThreshold) {
      const wait = circuitBreakerThreshold - (now - lastFailureTime.current)
      reconnectTimeoutRef.current = setTimeout(attemptReconnect, wait)
      return
    }
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      setIsConnected(false)
      setAuthType(null)
      setTimeout(() => {
        reconnectAttempts.current = 0
      }, 300000)
      return
    }
    const baseDelay = 5000
    const delay = Math.min(baseDelay * 2 ** reconnectAttempts.current, 120000)
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttempts.current++
      lastFailureTime.current = Date.now()
      connectRef.current()
    }, delay)
  }, [enabled])

  const connect = useCallback(
    async (forceConnect = false) => {
      if (isKickedRef.current) return
      const currentTenantId = tenantIdRef.current
      if (!forceConnect && (!enabled || !currentTenantId)) return
      if (forceConnect && !currentTenantId) return
      if (import.meta.env.DEV) {
        console.log('🔌 Connect proceeding with:', {
          forceConnect,
          enabled,
          currentTenantId,
        })
      }
      if (isConnectingRef.current) return
      const connectionId = `${currentTenantId}-${Date.now()}`
      connectionIdRef.current = connectionId
      isConnectingRef.current = true

      try {
        if (socketRef.current) {
          try {
            socketRef.current.removeAllListeners()
            socketRef.current.disconnect()
            socketRef.current.close()
          } catch (e) {
            console.warn('Error closing existing socket:', e)
          }
          socketRef.current = null
        }
        if (connectionIdRef.current !== connectionId) return

        const authStatus = detectAuthStatus()
        let token: string | null = null
        let connectionType: 'authenticated' | 'anonymous'
        if (authStatus.isAuthenticated && authStatus.token) {
          token = authStatus.token
          connectionType = 'authenticated'
        } else {
          token = await getAnonymousToken(currentTenantId)
          connectionType = 'anonymous'
        }
        if (!token) {
          setIsConnected(false)
          attemptReconnect()
          return
        }
        setAuthType(connectionType)

        const socket = io('/', {
          transports: ['websocket', 'polling'],
          upgrade: true,
          reconnection: false,
          query: {
            tenantId: currentTenantId,
            type: connectionType === 'authenticated' ? 'full_features' : 'content_updates',
            token,
          },
          timeout: 20000,
          withCredentials: connectionType === 'authenticated',
          ...(connectionType === 'authenticated' ? { auth: { token } } : {}),
        })

        socket.on('connect', () => {
          if (connectionIdRef.current !== connectionId) {
            socket.disconnect()
            return
          }
          if (import.meta.env.DEV) {
            console.log('✅ WebSocket connected', {
              socketId: socket.id,
              tenantId: currentTenantId,
              authType: connectionType,
            })
          }
          ;(window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket = socket
          setIsConnected(true)
          setAuthType(connectionType)
          isConnectingRef.current = false
          reconnectAttempts.current = 0
          lastFailureTime.current = 0
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
          }
          startPresencePing(socket, connectionType)
          socket.emit('join-channel', {
            channel: `content_updates_${currentTenantId}`,
            userInfo: {
              tenantId: currentTenantId,
              authType: connectionType,
            },
          })
        })

        socket.on('content_update', (data: ContentUpdateData) => {
          const msgTenantId = data.tenantId ?? (data.content as { tenantId?: string })?.tenantId
          const currentTenant = tenantIdRef.current
          const isForCurrentTenant = currentTenant && msgTenantId === currentTenant

          if (data.contentType === 'announcement_send_now' && isForCurrentTenant) {
            if (import.meta.env.DEV) console.log('📢 Announcement send-now received, dispatching refetch event')
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('announcement_refetch', { detail: data.content }))
            }, 0)
          }

          if (data.contentType === 'deposit_accounts' && isForCurrentTenant) {
            if (import.meta.env.DEV) console.log('🏦 Deposit accounts update received, dispatching refetch event')
            window.dispatchEvent(
              new CustomEvent('deposit_accounts_updated', {
                detail: { tenantId: currentTenant, timestamp: data.timestamp },
              })
            )
          }

          if (data.contentType === 'withdrawal_settings' && isForCurrentTenant) {
            if (import.meta.env.DEV) console.log('⚙️ Withdrawal/deposit channels update received, dispatching refetch event')
            window.dispatchEvent(
              new CustomEvent('deposit_channels_updated', {
                detail: { tenantId: currentTenant, content: data.content, timestamp: data.timestamp },
              })
            )
          }

          if (data.contentType === 'decimal_deposit' && isForCurrentTenant && data.content) {
            const payload = data.content as { type?: string; reservationId?: string; userId?: string; cancelledBy?: string }
            if (import.meta.env.DEV) console.log('💰 Decimal deposit update received', payload)
            window.dispatchEvent(
              new CustomEvent('decimal_deposit_updated', {
                detail: { ...payload, tenantId: currentTenant, timestamp: data.timestamp },
              })
            )
          }

          if (data.contentType === 'prediction_events_updated' && isForCurrentTenant) {
            if (import.meta.env.DEV) console.log('🎯 Prediction events update received, dispatching refetch event')
            window.dispatchEvent(
              new CustomEvent('prediction_events_updated', {
                detail: {
                  ...(typeof data.content === 'object' && data.content !== null ? data.content as Record<string, unknown> : {}),
                  tenantId: currentTenant,
                  timestamp: data.timestamp,
                },
              })
            )
          }

          if (data.type === 'content_update' && data.content && !(data as ContentUpdateData & { tenantId?: string }).tenantId) {
            onContentUpdateRef.current(data.content)
            return
          }
          const roundData = (data.content as { round?: { tenantId?: string }; data?: { tenantId?: string } })?.round ??
            (data.content as { round?: { tenantId?: string }; data?: { tenantId?: string } })?.data ??
            data.content
          const roundTenantId = (roundData as { tenantId?: string })?.tenantId
          const isForCurrentTenantRound =
            currentTenant &&
            (msgTenantId === currentTenant || roundTenantId === currentTenant)
          if (!isForCurrentTenantRound) return
        const contentDetail: Record<string, unknown> & {
          content: unknown
          contentType: string | undefined
          tenantId: string
          updatedBy: string | undefined
          timestamp: string | undefined
        } = {
          ...(typeof data.content === 'object' && data.content !== null ? data.content as Record<string, unknown> : {}),
          content: data.content,
          contentType: data.contentType,
          tenantId: msgTenantId || roundTenantId || currentTenant,
          updatedBy: data.updatedBy,
          timestamp: data.timestamp,
        }
        window.dispatchEvent(
          new CustomEvent('content_update', {
            detail: contentDetail,
          })
        )
        if (data.contentType === 'lottery_rounds') {
          const roundPayload =
            (data.content as { round?: unknown; data?: unknown } | null)?.round ??
            (data.content as { round?: unknown; data?: unknown } | null)?.data ??
            data.content
          if (roundPayload) {
            window.dispatchEvent(
              new CustomEvent('lottery_round_updated', {
                detail: {
                  type: typeof contentDetail.type === 'string' ? contentDetail.type : 'lottery_round_update',
                  round: roundPayload,
                  contentType: 'lottery_rounds',
                  tenantId: msgTenantId || roundTenantId || currentTenant,
                  timestamp: data.timestamp,
                },
              })
            )
          }
        }
        if (typeof contentDetail.type === 'string' && contentDetail.type.trim()) {
          window.dispatchEvent(
            new CustomEvent(contentDetail.type, {
              detail: contentDetail,
            })
          )
        }
          if (data.content) onContentUpdateRef.current(data.content)
        })

        socket.on('affiliate_config_updated', (data: {
          tenantId?: string
          enabled?: boolean
          updatedBy?: string
          timestamp?: string
        }) => {
          const currentTenant = tenantIdRef.current
          if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
            return
          }

          window.dispatchEvent(
            new CustomEvent('affiliate_config_updated', {
              detail: {
                tenantId: data?.tenantId || currentTenant,
                enabled: data?.enabled === true,
                updatedBy: data?.updatedBy,
                timestamp: data?.timestamp,
              },
            })
          )
        })

        socket.on('game_control_updated', (data: { tenantId?: string; [key: string]: unknown }) => {
          const currentTenant = tenantIdRef.current
          if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
            return
          }

          window.dispatchEvent(
            new CustomEvent('game_control_updated', {
              detail: {
                ...data,
                tenantId: data?.tenantId || currentTenant,
              },
            })
          )
        })

        socket.on('user_service_access_updated', (data: { tenantId?: string; [key: string]: unknown }) => {
          const currentTenant = tenantIdRef.current
          if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
            return
          }

          window.dispatchEvent(
            new CustomEvent('user_service_access_updated', {
              detail: {
                ...data,
                tenantId: data?.tenantId || currentTenant,
              },
            })
          )
        })

        socket.on('popup_show', (data: { tenantId: string; popup: unknown; forcedBy?: string; timestamp?: string }) => {
          if (data.tenantId !== tenantIdRef.current) return
          window.dispatchEvent(
            new CustomEvent('popup_websocket_event', {
              detail: {
                type: 'popup_show',
                popup: data.popup,
                data,
                tenantId: data.tenantId,
              },
            })
          )
        })

        socket.on('popup_system_toggle', (data: { tenantId: string; enabled: boolean; toggledBy?: string; timestamp?: string }) => {
          if (data.tenantId !== tenantIdRef.current) return
          window.dispatchEvent(
            new CustomEvent('popup_websocket_event', {
              detail: {
                type: 'popup_system_toggle',
                data,
                tenantId: data.tenantId,
              },
            })
          )
        })

        socket.on('admin_popup_system_toggle', (data: { tenantId: string; enabled: boolean; toggledBy?: string; timestamp?: string }) => {
          if (data.tenantId !== tenantIdRef.current) return
          window.dispatchEvent(
            new CustomEvent('popup_websocket_event', {
              detail: {
                type: 'admin_popup_system_toggle',
                data,
                tenantId: data.tenantId,
              },
            })
          )
        })

        socket.on('popup_clear_sessions', (data: { tenantId: string; clearedBy?: string; timestamp?: string }) => {
          if (data.tenantId !== tenantIdRef.current) return
          window.dispatchEvent(
            new CustomEvent('popup_websocket_event', {
              detail: {
                type: 'popup_clear_sessions',
                data,
                tenantId: data.tenantId,
              },
            })
          )
        })

        socket.on('popup_refresh', (data: { tenantId: string; refreshedBy?: string; timestamp?: string }) => {
          if (data.tenantId !== tenantIdRef.current) return
          window.dispatchEvent(
            new CustomEvent('popup_websocket_event', {
              detail: {
                type: 'popup_refresh',
                data,
                tenantId: data.tenantId,
              },
            })
          )
        })

        socket.on('kicked', (data: { reason: string; message: string; timestamp?: string }) => {
          const msg = data.message || 'คุณถูกเตะออกจากระบบโดยผู้ดูแลระบบ'
          handleUserKicked(data.reason, msg)
        })

        socket.on('balance_notification', (data: BalanceNotificationData) => {
          if (import.meta.env.DEV) console.log('💰 Balance adjustment notification received:', data)
          const isIncrease = data.adjustmentType === 'increase'
          const formattedAmount = Math.abs(data.adjustmentAmount).toLocaleString()
          const titleColor = isIncrease ? '#10b981' : '#f59e0b'
          const categoryMap: Record<string, string> = {
            deposit: 'ฝากเงิน',
            bonus: 'โบนัส',
            other: 'อื่นๆ',
          }
          const categoryText = categoryMap[data.adjustmentCategory] || data.adjustmentCategory
          Swal.fire({
            icon: isIncrease ? 'success' : 'info',
            title: isIncrease ? '💰 ยอดเงินเพิ่มขึ้น' : '💳 ยอดเงินถูกหัก',
            html: `
              <div style="text-align: center; padding: 12px;">
                <div style="font-size: 22px; margin-bottom: 12px; color: ${titleColor};">
                  <strong>${isIncrease ? '+' : '-'}${formattedAmount} บาท</strong>
                </div>
                <div style="color: #666; margin-bottom: 8px; font-size: 14px;"><strong>ประเภท:</strong> ${categoryText}</div>
                ${data.newBalance !== 'unknown' ? `<div style="color: #999; font-size: 13px; border-top: 1px solid #eee; padding-top: 8px;">ยอดเงินปัจจุบัน: ${typeof data.newBalance === 'number' ? data.newBalance.toLocaleString() : data.newBalance} บาท</div>` : ''}
              </div>
            `,
            confirmButtonText: 'รับทราบ',
            confirmButtonColor: titleColor,
            timer: 8000,
            timerProgressBar: true,
          })
          window.dispatchEvent(new CustomEvent('balance_notification', { detail: data }))
        })

        socket.on('maintenance_mode_changed', (data: MaintenanceSocketEvent) => {
          const currentTenant = tenantIdRef.current
          if (data.tenantId !== null && data.tenantId !== undefined && data.tenantId !== currentTenant) {
            return
          }
          window.dispatchEvent(
            new CustomEvent('maintenance_mode_changed', {
              detail: {
                tenantId: data.tenantId ?? currentTenant,
                enabled: data.enabled === true,
                reason: data.reason,
                estimatedEnd: data.estimatedEnd ?? null,
                maintenanceStart: data.maintenanceStart ?? null,
              },
            })
          )
        })

        socket.on('maintenance_warning', (data: MaintenanceSocketEvent) => {
          const currentTenant = tenantIdRef.current
          if (data.tenantId !== null && data.tenantId !== undefined && data.tenantId !== currentTenant) {
            return
          }
          window.dispatchEvent(
            new CustomEvent('maintenance_warning', {
              detail: {
                tenantId: data.tenantId ?? currentTenant,
                reason: data.reason,
                estimatedEnd: data.estimatedEnd ?? null,
                maintenanceStart: data.maintenanceStart ?? null,
                countdownMinutes: data.countdownMinutes ?? 5,
              },
            })
          )
        })

        socket.on('auth_error', () => {
          setIsConnected(false)
          anonymousTokenCache = null
          attemptReconnect()
        })

        socket.on('disconnect', (reason: string) => {
          if ((window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket === socket) {
            (window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket = undefined
          }
          clearPresencePing()
          setIsConnected(false)
          if (isKickedRef.current) return
          if (reason !== 'io client disconnect') attemptReconnect()
        })

        socket.on('connect_error', () => {
          setIsConnected(false)
          if (!isKickedRef.current) attemptReconnect()
        })

        socketRef.current = socket
      } catch (err) {
        console.error('WebSocket connection failed:', err)
        isConnectingRef.current = false
        setIsConnected(false)
        if (!isKickedRef.current) attemptReconnect()
      } finally {
        if (connectionIdRef.current === connectionId) {
          isConnectingRef.current = false
        }
      }
    },
    [enabled, detectAuthStatus, handleUserKicked]
  )

  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  const disconnect = useCallback(() => {
    clearPresencePing()
    if (socketRef.current) {
      try {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current.close()
      } catch (e) {
        console.warn('Error closing socket:', e)
      }
      socketRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    reconnectAttempts.current = 0
    lastFailureTime.current = 0
    isConnectingRef.current = false
    connectionIdRef.current = ''
    isKickedRef.current = false
    setIsConnected(false)
    setAuthType(null)
    if (import.meta.env.DEV) console.log('🔌 WebSocket fully disconnected and cleaned up')
  }, [clearPresencePing])

  useEffect(() => {
    if (enabled && tenantIdRef.current && !isConnectingRef.current && !socketRef.current) {
      if (import.meta.env.DEV) {
        console.log('🚀 Initial WebSocket connection for tenant:', tenantIdRef.current)
      }
      connect()
    }
    return () => disconnect()
  }, [enabled])

  // After login: reconnect with JWT so websocket-gateway emits user_online (admin sees user online)
  useEffect(() => {
    const handler = () => {
      if (tenantIdRef.current && connectRef.current) {
        if (import.meta.env.DEV) console.log('🔄 [useSmartWebSocket] Reconnecting after login (JWT) for admin online status')
        connectRef.current(true)
      }
    }
    window.addEventListener('huay_websocket_reconnect', handler)
    return () => window.removeEventListener('huay_websocket_reconnect', handler)
  }, [])

  return { isConnected, authType, reconnect: connect, disconnect }
}

export const getWebSocketConnection = () => {
  return (window as unknown as { __huayWebSocket?: Socket }).__huayWebSocket || null
}
